import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { NavigationStart, Router } from '@angular/router';
import { Observable, catchError, filter, firstValueFrom, map, of, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppConstants } from 'src/app/app-constants';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { IAccountFeatureKey, IAccountFeatureMessage } from 'src/app/interfaces/iaccount-feature';
import { selectMessageForLanguage } from 'src/app/utils/account-feature-message.util';

// Evaluates the current account's Product Features at runtime — separate from FeatureFlagsService's
// Split.io engineering flags. Callable by any authenticated role.
@Injectable({
  providedIn: 'root',
})
export class AccountFeatureService {
  private backendUrl: string = environment.backendUrl;
  // Shared for the whole navigation (guard + layout both check the same keys); cleared on the next
  // navigation so a toggle change still takes effect without requiring a full reload.
  private activeFeatures$?: Observable<Partial<Record<IAccountFeatureKey, boolean>>>;
  // Shared the same way, so every avatar on a call screen reuses one request.
  private aiNoticeMessages$?: Observable<IAccountFeatureMessage[]>;

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    router: Router,
  ) {
    router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.activeFeatures$ = undefined;
      this.aiNoticeMessages$ = undefined;
    });
  }

  async isFeatureActive(key: IAccountFeatureKey): Promise<boolean> {
    const features = await firstValueFrom(this.fetchActiveFeatures());
    const active = features[key] === true;
    this.logger.log(`Account feature checked: ${key} = ${active}`);
    return active;
  }

  private fetchActiveFeatures(): Observable<Partial<Record<IAccountFeatureKey, boolean>>> {
    if (!this.activeFeatures$) {
      this.activeFeatures$ = this.http
        .get<Partial<Record<IAccountFeatureKey, boolean>>>(`${this.backendUrl}/accountFeature/findActiveFeatures`)
        .pipe(
          catchError(() => of({})),
          shareReplay(1),
        );
    }
    return this.activeFeatures$;
  }

  // Every message for the current account and feature; empty means none configured.
  findMessages(featureKey: IAccountFeatureKey): Observable<IAccountFeatureMessage[]> {
    const params = new HttpParams().set('featureKey', featureKey);
    return this.http
      .get<IAccountFeatureMessage[]>(`${this.backendUrl}/accountFeatureMessage/findForCurrentAccount`, { params })
      .pipe(catchError(() => of([])));
  }

  // Entry point for runtime pages: the message for the learner's language, English, or none.
  getResultMessageText(featureKey: IAccountFeatureKey): Observable<string | null> {
    return this.findMessages(featureKey).pipe(map((messages) => selectMessageForLanguage(messages)));
  }

  // Every AI notice message for the current account; empty means the account shows none.
  getAiNoticeMessages(): Observable<IAccountFeatureMessage[]> {
    if (!this.aiNoticeMessages$) {
      this.aiNoticeMessages$ = this.findMessages(AppConstants.ACCOUNT_FEATURE_AI_INTERACTION_NOTIFICATION).pipe(shareReplay(1));
    }
    return this.aiNoticeMessages$;
  }
}
